/**
 * Authentication Service
 */
import apiClient from './apiClient';
import { API_BASE_URL } from '../config/api';
import { storage } from '../adapters/storage';
import { addDebugLog } from '../components/DebugPanel';

// Mobile detection
const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// Debug logging function
const debugLog = (entry: any) => {
  addDebugLog(entry);
};

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface LoginResponse {
  access: string;
  refresh: string;
  deve_trocar_senha?: boolean;
}

export interface User {
  id: number;
  email: string;
  username: string;
  first_name?: string;
  last_name?: string;
  role?: string;
  hospital_id?: number;
}

/**
 * Login user
 */
export const login = async (credentials: LoginCredentials): Promise<LoginResponse> => {
  const startTime = Date.now();

  debugLog({
    level: 'info',
    category: 'auth',
    message: '🔵 MOBILE DEBUG: Attempting login...',
    data: {
      email: credentials.email,
      passwordLength: credentials.password.length,
      apiBaseUrl: API_BASE_URL,
      isMobile: isMobileDevice(),
      timestamp: new Date().toISOString()
    }
  });

  try {
    // Check network connectivity first
    if (!navigator.onLine) {
      throw new Error('Device appears to be offline. Please check your network connection.');
    }

    debugLog({
      level: 'info',
      category: 'auth',
      message: '🔵 MOBILE DEBUG: Network connectivity confirmed',
      data: {
        online: navigator.onLine,
        connection: (navigator as any).connection ? {
          effectiveType: (navigator as any).connection.effectiveType,
          downlink: (navigator as any).connection.downlink,
          rtt: (navigator as any).connection.rtt
        } : 'unknown'
      }
    });

    // Use fetch directly to avoid axios transformation issues
    debugLog({
      level: 'info',
      category: 'auth',
      message: '🔵 MOBILE DEBUG: Making login request...',
      data: {
        url: `${API_BASE_URL}/auth/login`,
        method: 'POST',
        contentType: 'application/json'
      }
    });

    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: credentials.email,
        password: credentials.password,
      }),
    });

    const duration = Date.now() - startTime;

    if (!response.ok) {
      const error = await response.json();

      debugLog({
        level: 'error',
        category: 'auth',
        message: `❌ MOBILE DEBUG: Login failed (${response.status})`,
        data: {
          status: response.status,
          statusText: response.statusText,
          error: error.detail || error.message || 'Login failed',
          duration: `${duration}ms`,
          url: `${API_BASE_URL}/auth/login`,
          headers: Object.fromEntries(response.headers.entries())
        }
      });

      throw new Error(error.detail || error.message || 'Login failed');
    }

    const data = await response.json();
    const { access, refresh } = data;

    debugLog({
      level: 'success',
      category: 'auth',
      message: `✅ MOBILE DEBUG: Login successful!`,
      data: {
        accessTokenLength: access.length,
        refreshTokenLength: refresh.length,
        duration: `${duration}ms`,
        url: `${API_BASE_URL}/auth/login`,
        tokenReceived: true
      }
    });

    // Store tokens using cross-platform storage
    debugLog({
      level: 'info',
      category: 'storage',
      message: '💾 Storing tokens in storage...',
      data: {
        accessTokenPrefix: access.substring(0, 20) + '...',
        refreshTokenPrefix: refresh.substring(0, 20) + '...',
        storageType: isMobileDevice() ? 'Capacitor' : 'localStorage'
      }
    });

    await storage.setItem('accessToken', access);
    await storage.setItem('refreshToken', refresh);

    // Verify tokens were stored
    const storedAccessToken = await storage.getItem('accessToken');
    const storedRefreshToken = await storage.getItem('refreshToken');

    debugLog({
      level: 'info',
      category: 'storage',
      message: '🔐 MOBILE DEBUG: Token storage verification',
      data: {
        accessTokenStored: !!storedAccessToken,
        accessTokenLength: storedAccessToken?.length || 0,
        refreshTokenStored: !!storedRefreshToken,
        refreshTokenLength: storedRefreshToken?.length || 0
      }
    });

    // Decode JWT to get user info (simple base64 decode)
    const payload = JSON.parse(atob(access.split('.')[1]));
    const user: User = {
      id: payload.user_id,
      email: payload.email,
      username: payload.email.split('@')[0],
      role: payload.role,
      hospital_id: payload.hospital_id,
    };

    debugLog({
      level: 'info',
      category: 'auth',
      message: '👤 MOBILE DEBUG: User info extracted from token',
      data: {
        userId: user.id,
        email: user.email,
        username: user.username,
        role: user.role,
        hospital_id: user.hospital_id,
        tokenPayload: {
          user_id: payload.user_id,
          email: payload.email,
          role: payload.role,
          hospital_id: payload.hospital_id,
          exp: payload.exp,
          iat: payload.iat
        }
      }
    });

    await storage.setItem('user', JSON.stringify(user));

    debugLog({
      level: 'success',
      category: 'auth',
      message: '✅ MOBILE DEBUG: Login process completed successfully',
      data: {
        totalDuration: `${Date.now() - startTime}ms`,
        userStored: true,
        tokensStored: true
      }
    });

    return { access, refresh, deve_trocar_senha: data.deve_trocar_senha };
  } catch (error: any) {
    const duration = Date.now() - startTime;

    debugLog({
      level: 'error',
      category: 'auth',
      message: `❌ MOBILE DEBUG: Login failed after ${duration}ms`,
      data: {
        error: error.message,
        stack: error.stack,
        duration: `${duration}ms`,
        url: `${API_BASE_URL}/auth/login`,
        isNetworkError: error.message.includes('fetch') || error.message.includes('network'),
        isTimeoutError: error.message.includes('timeout'),
        isOfflineError: error.message.includes('offline')
      }
    });

    // Enhanced error messages for mobile
    if (error.message.includes('fetch') || error.message.includes('network')) {
      throw new Error('Erro de conexão. Verifique sua internet e tente novamente.');
    }

    if (error.message.includes('offline')) {
      throw new Error('Device appears to be offline. Please check your internet connection.');
    }

    if (error.message.includes('timeout')) {
      throw new Error('Request timed out. The server may be busy. Please try again.');
    }

    throw new Error(error.message || 'Login failed');
  }
};

/**
 * Logout user
 */
export const logout = async (): Promise<void> => {
  await storage.removeItem('accessToken');
  await storage.removeItem('refreshToken');
  await storage.removeItem('user');
  window.location.href = '/login';
};

/**
 * Get current user from storage
 */
export const getCurrentUser = async (): Promise<User | null> => {
  const userStr = await storage.getItem('user');
  if (!userStr) return null;
  try {
    return JSON.parse(userStr);
  } catch {
    return null;
  }
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = async (): Promise<boolean> => {
  const token = await storage.getItem('accessToken');
  return !!token;
};

/**
 * Refresh access token
 */
export const refreshAccessToken = async (): Promise<string> => {
  const refreshToken = await storage.getItem('refreshToken');
  if (!refreshToken) {
    throw new Error('No refresh token');
  }

  try {
    const response = await apiClient.post<{ access: string }>('/auth/refresh', {
      refresh: refreshToken,
    });
    const { access } = response.data;
    await storage.setItem('accessToken', access);
    return access;
  } catch (error) {
    await logout();
    throw error;
  }
};

// ============================================================
// N90 — Simular Permissão (portado do Thesys)
// Troca o token ativo pelo token do usuário-alvo (emitido pelo backend com o
// claim `simulado_por`). Guarda o token REAL do admin para restaurar depois.
// O token simulado não tem refresh (expira em 30 min → cai no login).
// ============================================================
const REAL_ACCESS_KEY = 'accessToken_real';
const REAL_REFRESH_KEY = 'refreshToken_real';

/** Decodifica o payload do JWT ativo. */
export const decodeActiveToken = async (): Promise<any | null> => {
  try {
    const access = await storage.getItem('accessToken');
    if (!access) return null;
    return JSON.parse(atob(access.split('.')[1]));
  } catch {
    return null;
  }
};

/** true se o token ativo é de simulação. */
export const isSimulando = async (): Promise<boolean> => {
  const p = await decodeActiveToken();
  return !!(p && p.simulado_por);
};

/**
 * Inicia a simulação: pede o token do alvo, guarda o token real e ativa o
 * simulado. Retorna { user (alvo), simulacao }. NÃO recarrega a página (quem
 * chama decide) — mas remove o refresh token para o apiClient não renovar
 * silenciosamente para o admin durante a simulação.
 */
export const iniciarSimulacaoToken = async (userId: number): Promise<{ user: User; simulacao: any }> => {
  const resp = await apiClient.post('/auth/simular-permissoes', { user_id: userId });
  const access: string = resp.data.access;

  // Guarda a sessão real do admin (só na 1ª vez, para não sobrescrever com o simulado)
  const jaSimulando = await storage.getItem(REAL_ACCESS_KEY);
  if (!jaSimulando) {
    const realAccess = await storage.getItem('accessToken');
    const realRefresh = await storage.getItem('refreshToken');
    if (realAccess) await storage.setItem(REAL_ACCESS_KEY, realAccess);
    if (realRefresh) await storage.setItem(REAL_REFRESH_KEY, realRefresh);
  }

  // Ativa o token simulado; remove refresh (simulação expira em 30 min, sem renovar)
  await storage.setItem('accessToken', access);
  await storage.removeItem('refreshToken');

  const payload = JSON.parse(atob(access.split('.')[1]));
  const user: User = {
    id: payload.user_id,
    email: payload.email,
    username: (payload.email || '').split('@')[0],
    role: payload.role,
    hospital_id: payload.hospital_id,
  };
  await storage.setItem('user', JSON.stringify(user));
  return { user, simulacao: resp.data.simulacao };
};

/** Encerra a simulação: restaura o token real do admin. NÃO recarrega. */
export const encerrarSimulacaoToken = async (): Promise<void> => {
  const realAccess = await storage.getItem(REAL_ACCESS_KEY);
  const realRefresh = await storage.getItem(REAL_REFRESH_KEY);
  if (realAccess) await storage.setItem('accessToken', realAccess);
  if (realRefresh) await storage.setItem('refreshToken', realRefresh);
  await storage.removeItem(REAL_ACCESS_KEY);
  await storage.removeItem(REAL_REFRESH_KEY);
};

const authService = {
  login,
  logout,
  getCurrentUser,
  isAuthenticated,
  refreshAccessToken,
  iniciarSimulacaoToken,
  encerrarSimulacaoToken,
  isSimulando,
  decodeActiveToken,
};

export default authService;
